/**
 * WhatsApp Service - Handles both API and manual WhatsApp integration
 */

import { format, differenceInDays } from 'date-fns';
import { getApiUrl } from '../config/api';

interface Resident {
  id: number;
  residentName: string;
  bedNumber: string;
  phoneNumber: string;
  dueDate: Date;
  amount: number;
  paidAmount: number;
  paymentStatus: string;
  gracePeriodEnd: Date;
}

interface WhatsAppConfig {
  apiConfigured: boolean;
  apiToken?: string;
  manualModeEnabled: boolean;
  urlScheme: string;
}

interface ReminderTemplate {
  type: 'due_reminder' | 'overdue_reminder' | 'final_notice' | 'payment_received';
  message: string;
}

class WhatsAppService {
  private config: WhatsAppConfig | null = null;
  private baseUrl = '/api/whatsapp';

  /**
   * Check WhatsApp API configuration status (T028: GET /api/whatsapp/config-status)
   */
  async checkConfiguration(): Promise<WhatsAppConfig> {
    try {
      const response = await fetch(getApiUrl(`${this.baseUrl}/config-status`));
      if (response.ok) {
        const json = await response.json();
        const config: WhatsAppConfig = json.data ?? json;
        this.config = config;
        return config;
      } else {
        this.config = {
          apiConfigured: false,
          manualModeEnabled: true,
          urlScheme: 'https://wa.me/',
        };
        return this.config;
      }
    } catch (error) {
      console.error('Failed to check WhatsApp configuration:', error);
      this.config = {
        apiConfigured: false,
        manualModeEnabled: true,
        urlScheme: 'https://wa.me/',
      };
      return this.config;
    }
  }

  /**
   * Generate reminder message based on payment status
   */
  generateReminderMessage(resident: Resident, template?: 'standard' | 'urgent' | 'final'): string {
    const daysOverdue = differenceInDays(new Date(), resident.dueDate);
    const outstanding = resident.amount - resident.paidAmount;
    const isOverdue = daysOverdue > 0;
    const templateType = template || (isOverdue ? 'urgent' : 'standard');

    let message = `Hello ${resident.residentName},\n\n`;

    switch (templateType) {
      case 'standard':
        message += `This is a friendly reminder that your rent payment of ₹${outstanding.toLocaleString()} for bed ${resident.bedNumber} is due on ${format(resident.dueDate, 'dd MMM yyyy')}.\n\n`;
        message += `Please make the payment at your convenience. Grace period ends on ${format(resident.gracePeriodEnd, 'dd MMM yyyy')}.\n\n`;
        break;

      case 'urgent':
        message += `⚠️ URGENT: Your rent payment of ₹${outstanding.toLocaleString()} for bed ${resident.bedNumber} was due on ${format(resident.dueDate, 'dd MMM yyyy')} and is now ${daysOverdue} day(s) overdue.\n\n`;
        message += `Please make the payment immediately to avoid late fees. Grace period ends on ${format(resident.gracePeriodEnd, 'dd MMM yyyy')}.\n\n`;
        break;

      case 'final':
        message += `🚨 FINAL NOTICE: Your rent payment of ₹${outstanding.toLocaleString()} for bed ${resident.bedNumber} is ${daysOverdue} day(s) overdue.\n\n`;
        message += `Immediate payment is required to avoid administrative action. Please contact the office immediately.\n\n`;
        break;
    }

    message += `Thank you for your cooperation.\n`;
    message += `Brahmachaitanya Girls Hostel\n`;
    message += `📞 For queries: Contact Office`;

    return message;
  }

  /**
   * Generate WhatsApp URL for manual sending
   */
  generateWhatsAppUrl(phoneNumber: string, message: string): string {
    const cleanPhoneNumber = phoneNumber.replace(/[^\d]/g, '');
    const encodedMessage = encodeURIComponent(message);
    const scheme = this.config?.urlScheme || 'https://wa.me/';
    
    return `${scheme}${cleanPhoneNumber}?text=${encodedMessage}`;
  }

  /**
   * Send manual reminder: calls API for whatsappUrl, then opens WhatsApp (T030).
   * Falls back to local URL generation if API fails.
   */
  async sendManualReminder(resident: Resident, template?: 'standard' | 'urgent' | 'final'): Promise<void> {
    let whatsappUrl: string = '';
    try {
      const payload = {
        residentId: resident.id,
        residentName: resident.residentName,
        bedNumber: resident.bedNumber,
        phoneNumber: resident.phoneNumber,
        dueDate: resident.dueDate instanceof Date ? resident.dueDate.toISOString().split('T')[0] : String(resident.dueDate),
        amount: resident.amount,
        paidAmount: resident.paidAmount,
        paymentStatus: resident.paymentStatus,
        gracePeriodEnd: resident.gracePeriodEnd instanceof Date ? resident.gracePeriodEnd.toISOString().split('T')[0] : String(resident.gracePeriodEnd),
        messageTemplate: template || 'standard',
      };
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const response = await fetch(getApiUrl(`${this.baseUrl}/manual-reminder`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const result = await response.json();
        whatsappUrl = result.data?.whatsappUrl;
      }
      if (!whatsappUrl) throw new Error('No URL in response');
    } catch (err) {
      console.warn('Manual reminder API failed, using local generation:', err);
      const message = this.generateReminderMessage(resident, template);
      whatsappUrl = this.generateWhatsAppUrl(resident.phoneNumber, message);
    }
    if (!whatsappUrl) {
      throw new Error('Could not generate WhatsApp URL');
    }
    if (this.isWhatsAppAvailable()) {
      window.open(whatsappUrl, '_blank');
    } else {
      throw new Error('WhatsApp is not available on this device');
    }
  }

  /**
   * Send bulk manual reminders with delay (calls API per resident, opens WhatsApp URL)
   */
  async sendBulkManualReminders(
    residents: Resident[],
    template?: 'standard' | 'urgent' | 'final',
    delay: number = 1000
  ): Promise<void> {
    for (let i = 0; i < residents.length; i++) {
      const resident = residents[i];
      if (i > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
      try {
        await this.sendManualReminder(resident, template);
      } catch (error) {
        console.error(`Failed to send reminder to ${resident.residentName}:`, error);
      }
    }
  }

  /**
   * Send automated reminder via API (when configured)
   */
  async sendAutomatedReminder(resident: Resident, template?: 'standard' | 'urgent' | 'final'): Promise<boolean> {
    if (!this.config?.apiConfigured) {
      throw new Error('WhatsApp API is not configured. Use manual reminder instead.');
    }

    try {
      const message = this.generateReminderMessage(resident, template);
      const response = await fetch(getApiUrl(`${this.baseUrl}/send-reminder`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: resident.phoneNumber,
          message,
          residentId: resident.id,
          reminderType: template || 'standard',
        }),
      });

      if (response.ok) {
        const result = await response.json();
        return result.success;
      } else {
        throw new Error(`API request failed: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Failed to send automated reminder:', error);
      return false;
    }
  }

  /**
   * Get available reminder templates
   */
  getReminderTemplates(): ReminderTemplate[] {
    return [
      {
        type: 'due_reminder',
        message: 'Standard reminder for upcoming due date',
      },
      {
        type: 'overdue_reminder', 
        message: 'Urgent reminder for overdue payment',
      },
      {
        type: 'final_notice',
        message: 'Final notice for severely overdue payment',
      },
    ];
  }

  /**
   * Check if WhatsApp is available on the device
   */
  isWhatsAppAvailable(): boolean {
    // WhatsApp Web works on desktop, mobile app works on mobile
    return true; // WhatsApp Web is generally available on all devices
  }

  /**
   * Validate phone number format
   */
  isValidPhoneNumber(phoneNumber: string): boolean {
    // Remove all non-digit characters
    const cleanNumber = phoneNumber.replace(/[^\d]/g, '');
    
    // Check if it's a valid Indian mobile number (10 digits) or international format
    return cleanNumber.length >= 10 && cleanNumber.length <= 15;
  }

  /**
   * Format phone number for display
   */
  formatPhoneNumber(phoneNumber: string): string {
    const cleaned = phoneNumber.replace(/[^\d+]/g, '');
    
    if (cleaned.startsWith('+91') && cleaned.length === 13) {
      // Indian number: +91 XXXXX XXXXX
      return `+91 ${cleaned.substring(3, 8)} ${cleaned.substring(8)}`;
    } else if (cleaned.length === 10) {
      // Assume Indian number without country code
      return `+91 ${cleaned.substring(0, 5)} ${cleaned.substring(5)}`;
    }
    
    return phoneNumber; // Return as-is if format doesn't match
  }

  /**
   * Get reminder statistics
   */
  async getReminderStats(): Promise<{
    totalSent: number;
    deliveryRate: number;
    responseRate: number;
    lastSentDate: Date | null;
  }> {
    try {
      const response = await fetch(getApiUrl(`${this.baseUrl}/stats`));
      if (response.ok) {
        return await response.json();
      }
      
      return {
        totalSent: 0,
        deliveryRate: 0,
        responseRate: 0,
        lastSentDate: null,
      };
    } catch (error) {
      console.error('Failed to get reminder stats:', error);
      return {
        totalSent: 0,
        deliveryRate: 0,
        responseRate: 0,
        lastSentDate: null,
      };
    }
  }
}

// Export singleton instance
export const whatsappService = new WhatsAppService();
export default whatsappService;